import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { communityAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  ChevronLeft, Trophy, MapPin, Clock, Heart, Send,
  Loader2, TrendingUp, Users, Sparkles, PartyPopper
} from 'lucide-react';
import { toast } from 'sonner';

const CommunityPage = () => {
  const navigate = useNavigate();
  const { user, isSubscribed } = useAuth();
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [filterProvince, setFilterProvince] = useState('');

  const [formData, setFormData] = useState({
    job_title: '',
    company_type: '',
    location: '',
    time_to_job: '',
    salary: '',
    name: '',
    message: '',
    is_anonymous: true
  });

  const provinces = [
    'Gauteng', 'Western Cape', 'KwaZulu-Natal', 'Eastern Cape',
    'Free State', 'Limpopo', 'Mpumalanga', 'North West', 'Northern Cape'
  ];

  const companyTypes = [
    'Retail', 'Office/Corporate', 'Hospitality', 'Healthcare',
    'IT/Tech', 'Manufacturing', 'Government', 'Education', 'Other'
  ];

  useEffect(() => {
    loadFeed();
  }, [filterProvince]);

  const loadFeed = async () => {
    try {
      const response = await communityAPI.getFeed(30, filterProvince || null);
      setPosts(response.data.posts);
      setStats(response.data.stats);
    } catch (error) {
      console.error('Failed to load feed');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.job_title || !formData.location) {
      toast.error('Please fill in job title and location');
      return;
    }

    setSubmitting(true);
    try {
      await communityAPI.postSuccess(formData);
      toast.success('Congratulations! Your success story has been shared!');
      setShowForm(false);
      setFormData({
        job_title: '',
        company_type: '',
        location: '',
        time_to_job: '',
        salary: '',
        name: '',
        message: '',
        is_anonymous: true
      });
      loadFeed();
    } catch (error) {
      toast.error('Failed to post. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Track which posts the user has liked (for visual feedback)
  const [likedPosts, setLikedPosts] = useState(new Set());

  const handleLike = async (postId) => {
    // Prevent double-liking
    if (likedPosts.has(postId)) {
      return;
    }
    
    // Update UI immediately for better UX
    setPosts(posts.map(p => 
      p.id === postId ? { ...p, likes: (p.likes || 0) + 1 } : p
    ));
    setLikedPosts(new Set([...likedPosts, postId]));
    
    // For seed posts (generated ones), just update locally - don't call API
    if (postId.startsWith('seed_')) {
      return;
    }
    
    // For real posts, also update in database
    try {
      await communityAPI.likePost(postId);
    } catch (error) {
      console.error('Failed to like');
      // Don't revert UI - the like still shows for this session
    }
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-br from-emerald-600 via-emerald-500 to-teal-500 text-white px-4 lg:px-8 py-8">
        <div className="max-w-4xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="text-emerald-100 hover:text-white hover:bg-emerald-700 mb-4"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Dashboard
          </Button>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Trophy className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold">Community Wins</h1>
              <p className="text-emerald-100">Celebrate job successes from across South Africa</p>
            </div>
          </div>

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
              <div className="bg-white/10 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold">{stats.this_week}</p>
                <p className="text-sm text-emerald-100">This Week</p>
              </div>
              <div className="bg-white/10 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold">{stats.this_month}</p>
                <p className="text-sm text-emerald-100">This Month</p>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="py-6 px-4 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Share Your Win CTA */}
          {!showForm ? (
            <div className="bg-gradient-to-r from-amber-500 to-amber-600 rounded-2xl p-6 mb-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <PartyPopper className="w-8 h-8" />
                  <div>
                    <h2 className="font-bold text-lg">Got a job? Share your win!</h2>
                    <p className="text-amber-100 text-sm">Inspire others in your community</p>
                  </div>
                </div>
                <Button 
                  onClick={() => setShowForm(true)}
                  className="bg-white text-amber-600 hover:bg-amber-50 font-semibold"
                >
                  Share Your Story
                </Button>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
              <h2 className="font-bold text-lg text-slate-900 mb-4 flex items-center gap-2">
                <PartyPopper className="w-5 h-5 text-amber-500" />
                Share Your Success!
              </h2>
              
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Job Title *</label>
                  <Input
                    value={formData.job_title}
                    onChange={(e) => setFormData({...formData, job_title: e.target.value})}
                    placeholder="e.g., Receptionist, Sales Assistant"
                    className="h-11"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Industry</label>
                  <Select value={formData.company_type} onValueChange={(v) => setFormData({...formData, company_type: v})}>
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {companyTypes.map((type) => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Province *</label>
                  <Select value={formData.location} onValueChange={(v) => setFormData({...formData, location: v})}>
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="Select province" />
                    </SelectTrigger>
                    <SelectContent>
                      {provinces.map((prov) => (
                        <SelectItem key={prov} value={prov}>{prov}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Time to Get Job</label>
                  <Select value={formData.time_to_job} onValueChange={(v) => setFormData({...formData, time_to_job: v})}>
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="How long?" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1 week">1 week</SelectItem>
                      <SelectItem value="2 weeks">2 weeks</SelectItem>
                      <SelectItem value="3 weeks">3 weeks</SelectItem>
                      <SelectItem value="1 month">1 month</SelectItem>
                      <SelectItem value="2 months">2 months</SelectItem>
                      <SelectItem value="3+ months">3+ months</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Starting Salary (Optional)</label>
                  <Input
                    value={formData.salary}
                    onChange={(e) => setFormData({...formData, salary: e.target.value})}
                    placeholder="e.g., R6,500"
                    className="h-11"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Your Name (Optional)</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="e.g., Thabo Mnisi → shown as Thabo M."
                    className="h-11"
                    disabled={formData.is_anonymous}
                  />
                  <p className="text-xs text-slate-500 mt-1">Name is shortened for privacy</p>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-1">Your Message (Optional)</label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  placeholder="Share a tip or encouragement for others..."
                  className="min-h-[80px]"
                />
              </div>

              <div className="flex items-center gap-2 mb-4">
                <input
                  type="checkbox"
                  id="anonymous"
                  checked={formData.is_anonymous}
                  onChange={(e) => setFormData({...formData, is_anonymous: e.target.checked, name: e.target.checked ? '' : formData.name})}
                  className="rounded"
                />
                <label htmlFor="anonymous" className="text-sm text-slate-600">Post anonymously (hide name)</label>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="flex-1 h-11 bg-emerald-600 hover:bg-emerald-700"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Share My Win
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowForm(false)}
                  className="h-11"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Filter */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-500" />
              Recent Wins
            </h3>
            <Select value={filterProvince || "all"} onValueChange={(v) => setFilterProvince(v === "all" ? "" : v)}>
              <SelectTrigger className="w-40 h-9">
                <SelectValue placeholder="All Provinces" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Provinces</SelectItem>
                {provinces.map((prov) => (
                  <SelectItem key={prov} value={prov}>{prov}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Posts Feed */}
          {posts.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
              <Trophy className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600">No success stories yet. Be the first to share!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {posts.map((post) => (
                <div 
                  key={post.id}
                  className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                        <Trophy className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900">{post.job_title}</h4>
                        <div className="flex items-center gap-2 text-sm text-slate-500 flex-wrap">
                          {post.salary && (
                            <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded font-medium">{post.salary}</span>
                          )}
                          {post.company_type && (
                            <span className="bg-slate-100 px-2 py-0.5 rounded">{post.company_type}</span>
                          )}
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {post.location}
                          </span>
                        </div>
                      </div>
                    </div>
                    <span className="text-xs text-slate-400">{getTimeAgo(post.created_at)}</span>
                  </div>

                  {post.time_to_job && (
                    <div className="flex items-center gap-1 text-sm text-emerald-600 mb-2">
                      <Clock className="w-4 h-4" />
                      Got job in {post.time_to_job}
                    </div>
                  )}

                  {post.message && (
                    <p className="text-slate-700 text-sm bg-slate-50 rounded-lg p-3 mb-3">
                      "{post.message}"
                    </p>
                  )}

                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => handleLike(post.id)}
                      disabled={likedPosts.has(post.id)}
                      className={`flex items-center gap-1 text-sm transition-colors ${
                        likedPosts.has(post.id) 
                          ? 'text-red-500 cursor-default' 
                          : 'text-slate-500 hover:text-red-500'
                      }`}
                    >
                      <Heart className={`w-4 h-4 ${likedPosts.has(post.id) ? 'fill-red-500' : ''}`} />
                      {post.likes || 0}
                    </button>
                    <span className="text-xs text-slate-400">
                      {post.display_name ? post.display_name : (post.is_anonymous ? 'Anonymous' : 'Career Step User')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Location Stats */}
          {stats?.by_location && stats.by_location.length > 0 && (
            <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-emerald-500" />
                Wins by Province
              </h3>
              <div className="space-y-3">
                {stats.by_location.map((loc, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-slate-700">{loc.location}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-32 bg-slate-100 rounded-full h-2">
                        <div 
                          className="bg-emerald-500 h-2 rounded-full"
                          style={{ width: `${Math.min((loc.count / stats.this_month) * 100, 100)}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-slate-900 w-8">{loc.count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default CommunityPage;
